import axios from 'axios';
import { MAX_FILE_SIZE_MB } from './fileUploadLimits';

// מקור אמת מרכזי אחד לטיפול בשגיאות API בכל המערכת.
// המשתמש מקבל הודעה עברית מבוקרת בלבד; הקונסול מקבל פירוט מסונן לאבחון.

const DEFAULT_MSG = 'אירעה שגיאה. נסה שוב מאוחר יותר.';

/** טווח האותיות העבריות ב-Unicode — משמש לזיהוי הודעות שנועדו למשתמש */
const HEBREW_RE = /[֐-׿]/;

/** מחלץ הודעת שגיאה מבוקרת מתשובת השרת — לעולם לא מחזיר "[object Object]" */
export function extractErrorMessage(error: unknown, fallback: string = DEFAULT_MSG): string {
  if (axios.isAxiosError(error)) {
    // 413 נדחה ע"י ה-pipeline לפני שהקוד רץ ולכן חוזר ללא גוף הודעה —
    // נבדק לפני ניתוח הגוף כדי שלא ייפול להודעה כללית
    if (error.response?.status === 413) {
      return `הקובץ גדול מדי (מקסימום ${MAX_FILE_SIZE_MB}MB)`;
    }

    const data = error.response?.data as
      | { message?: unknown; error?: unknown; errors?: Record<string, unknown> }
      | string
      | undefined;

    // אובייקט עם שדה message
    if (data && typeof data === 'object' && typeof data.message === 'string' && data.message.trim()) {
      return data.message;
    }

    // אובייקט עם שדה error (תבנית חלופית בחלק מה-endpoints)
    if (data && typeof data === 'object' && typeof data.error === 'string' && data.error.trim()) {
      return data.error;
    }

    // תשובת string ישירה מהשרת — רק אם היא בעברית. טקסט לועזי הוא כמעט תמיד הודעת
    // חריגה גולמית מ-.NET ואין להציג אותו למשתמש; במקרה כזה נופלים להודעה העברית הכללית.
    if (typeof data === 'string' && HEBREW_RE.test(data)) {
      return data;
    }

    // ValidationProblemDetails — מילון שגיאות ולידציה
    if (data && typeof data === 'object' && data.errors) {
      const msgs = Object.values(data.errors)
        .flat()
        .filter((m): m is string => typeof m === 'string' && m.trim().length > 0);
      if (msgs.length) return msgs.join(' • ');
    }

    // אין תגובה מהשרת — בעיית רשת/חיבור
    if (error.request && !error.response) {
      return 'שגיאה בחיבור לשרת. בדוק את החיבור לאינטרנט.';
    }

    // קודי סטטוס נפוצים ללא גוף הודעה
    if (error.response?.status === 403) {
      return 'אין לך הרשאה לבצע פעולה זו';
    }
    if (error.response?.status === 401) {
      return 'פג תוקף ההתחברות. אנא התחבר מחדש.';
    }
  }

  return fallback;
}

/** לוג מסונן לקונסול — ללא stack/שמות פונקציות/פרטים פנימיים */
export function logError(error: unknown, context?: string): void {
  if (axios.isAxiosError(error)) {
    console.error('[API Error]', {
      context,
      status: error.response?.status,
      method: error.config?.method?.toUpperCase(),
      url: error.config?.url,
      serverMessage: extractErrorMessage(error),
    });
  } else {
    console.error('[Error]', context, error);
  }
}

/** נקודת כניסה אחת: מלוגג לקונסול ומחזיר הודעה מבוקרת ל-toast */
export function handleApiError(error: unknown, fallback?: string): string {
  logError(error);
  return extractErrorMessage(error, fallback);
}
